import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import api from "@/lib/api";

const STATUS = ["new", "contacted", "converted", "lost"];
const badge = { new: "bg-sky", contacted: "bg-sun", converted: "bg-green-200", lost: "bg-slate-200" };

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/leads/${id}`, { status }); load(); } catch { toast.error("Failed"); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter((l) => [l.name, l.email, l.occasion, l.source].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [leads, q]);

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Leads</h1>
          <p className="text-ink/60">{leads.length} captured leads.</p>
        </div>
        <button onClick={exportCsv} disabled={leads.length === 0} className="brut-btn !py-2 !px-4 bg-sun text-sm disabled:opacity-40" data-testid="leads-export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…"
          className="brut-input !py-2 !pl-9 w-full text-sm" data-testid="leads-search" />
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <div className="brut-card p-6 text-ink/60">No leads yet. Share your site to start capturing!</div>}
        {filtered.map((l) => (
          <div key={l.id} className="brut-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
            <div>
              <div className="font-display font-bold text-lg">{l.name} <span className="text-ink/40 text-sm">· {l.email}</span></div>
              <div className="text-sm text-ink/60">
                {l.occasion && <span className="tag-pill bg-grape/30 mr-2">For: {l.occasion}</span>}
                <span className="tag-pill bg-white">via {l.source}</span>
                <span className="ml-2 text-ink/40">{new Date(l.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
              className={`brut-input !py-2 !w-auto text-sm font-semibold ${badge[l.status] || ""}`} data-testid={`lead-status-${l.id.slice(0, 8)}`}>
              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
